import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, RotateCcw, X, Sliders } from "lucide-react";

const LandFilter = ({
  selectedFilters,
  setSelectedFilters,
  budgetRange,
  setBudgetRange,
  sizeRange,
  setSizeRange,
  resetFilters
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [backupFilters, setBackupFilters] = useState({});
  const [backupBudgetRange, setBackupBudgetRange] = useState([]);
  const [backupSizeRange, setBackupSizeRange] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    typeOfProperty: true,
    bedrooms: true,
    budget: true,
    size: true,
    furnishedStatus: true,
    postedBy: true,
    availabilityStatus: true,
    amenities: true,
    reraApproved: true,
  });

  useEffect(() => {
    if (isOpen) {
      // Save current filters as backup when opening filter panel
      setBackupFilters({ ...selectedFilters });
      setBackupBudgetRange([...budgetRange]);
      setBackupSizeRange([...sizeRange]);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleFilter = (category, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((item) => item !== value)
        : [...prev[category], value],
    }));
  };

  

  const applyFilters = () => {
    setIsOpen(false);
  };

  const handleCancelFilter = () => {
    // Restore filters from backup
    setSelectedFilters({ ...backupFilters });
    setBudgetRange([...backupBudgetRange]);
    setSizeRange([...backupSizeRange]);
    setIsOpen(false);
  };

  const FilterSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 sm:py-4 px-4 sm:px-5 hover:bg-gray-50 transition-colors"
      >
        <span className="text-xs sm:text-sm md:text-base font-medium text-gray-900">
          {title}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 sm:px-5 pb-3 sm:pb-4">{children}</div>
      )}
    </div>
  );

  const FilterButton = ({ label, isSelected, onClick }) => (
    <button
      onClick={onClick}
      className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-2xl text-xs sm:text-sm transition-all ${
        isSelected
          ? "bg-blue-100 text-blue-700 border border-blue-300"
          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
      }`}
    >
      {isSelected ? (
        <span className="mr-1">✕</span>
      ) : (
        <span className="mr-1">✓</span>
      )}
      {label}
    </button>
  );

  const FilterContent = () => (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h2 className="text-[#031722] font-medium text-[28px] leading-[150%] tracking-[1%]">
          Filter
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={resetFilters}
            className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-700 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Reset</span>
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="overflow-y-auto">
        <FilterSection
          title="Budget"
          isExpanded={expandedSections.budget}
          onToggle={() => toggleSection("budget")}
        >
          <div className="space-y-3 sm:space-y-4">
            <div className="relative pt-1">
              <input
                type="range"
                min="100"
                max="10000000"
                step="100"
                value={budgetRange[0]}
                onChange={(e) =>
                  setBudgetRange([parseInt(e.target.value), budgetRange[1]])
                }
                className="absolute w-full h-2 bg-blue-200 rounded-2xl appearance-none cursor-pointer"
              />
              <input
                type="range"
                min="100"
                max="10000000"
                step="100"
                value={budgetRange[1]}
                onChange={(e) =>
                  setBudgetRange([budgetRange[0], parseInt(e.target.value)])
                }
                className="absolute w-full h-2 bg-transparent rounded-2xl appearance-none cursor-pointer"
              />
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mt-8 sm:mt-10">
              <span>₹{(budgetRange[0] / 100000).toFixed(2)}L</span>
              <span>
                ₹
                {budgetRange[1] >= 10000000
                  ? "10Cr"
                  : (budgetRange[1] / 100000).toFixed(2) + "L"}
              </span>
            </div>
          </div>
        </FilterSection>

        <FilterSection
          title="Size(sqft)"
          isExpanded={expandedSections.size}
          onToggle={() => toggleSection("size")}
        >
          <div className="space-y-3 sm:space-y-4">
            <div className="relative pt-1">
              <input
                type="range"
                min="100"
                max="10000"
                value={sizeRange[0]}
                onChange={(e) =>
                  setSizeRange([parseInt(e.target.value), sizeRange[1]])
                }
                className="absolute w-full h-2 bg-blue-200 rounded-2xl appearance-none cursor-pointer"
              />
              <input
                type="range"
                min="100"
                max="10000"
                value={sizeRange[1]}
                onChange={(e) =>
                  setSizeRange([sizeRange[0], parseInt(e.target.value)])
                }
                className="absolute w-full h-2 bg-transparent rounded-2xl appearance-none cursor-pointer"
              />
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mt-8 sm:mt-10">
              <span>{sizeRange[0]} sqft</span>
              <span>{sizeRange[1]} sqft</span>
            </div>
          </div>
        </FilterSection>

        <FilterSection
          title="Availability Status"
          isExpanded={expandedSections.availabilityStatus}
          onToggle={() => toggleSection("availabilityStatus")}
        >
          <div className="flex flex-wrap gap-2">
            {["Ready To Move", "Under Construction"].map((type) => (
              <FilterButton
                key={type}
                label={type}
                isSelected={selectedFilters.availabilityStatus.includes(type)}
                onClick={() => toggleFilter("availabilityStatus", type)}
              />
            ))}
          </div>
        </FilterSection>

        <FilterSection
          title="Amenities"
          isExpanded={expandedSections.amenities}
          onToggle={() => toggleSection("amenities")}
        >
          <div className="flex flex-wrap gap-2">
            {["Parking", "Lift", "Park", "Power Backup", "Gym"].map(
              (amenity) => (
                <FilterButton
                  key={amenity}
                  label={amenity}
                  isSelected={selectedFilters.amenities.includes(amenity)}
                  onClick={() => toggleFilter("amenities", amenity)}
                />
              )
            )}
          </div>
        </FilterSection>

        {/* Photos & Videos Toggles */}
        <div className="border-b border-gray-200 px-4 sm:px-5 py-3 sm:py-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-xs sm:text-sm md:text-base font-medium text-gray-900">
              Properties with photos
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={selectedFilters.withPhotos}
                onChange={(e) =>
                  setSelectedFilters((prev) => ({
                    ...prev,
                    withPhotos: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 sm:w-11 sm:h-6 rounded-2xl transition-colors ${
                  selectedFilters.withPhotos ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-2xl shadow-md transform transition-transform ${
                    selectedFilters.withPhotos
                      ? "translate-x-5 sm:translate-x-6"
                      : "translate-x-0.5"
                  } mt-0.5`}
                ></div>
              </div>
            </div>
          </label>
        </div>

        <div className="px-4 sm:px-5 py-3 sm:pb-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-xs sm:text-sm md:text-base font-medium text-gray-900">
              Properties with videos
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={selectedFilters.withVideos}
                onChange={(e) =>
                  setSelectedFilters((prev) => ({
                    ...prev,
                    withVideos: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 sm:w-11 sm:h-6 rounded-2xl transition-colors ${
                  selectedFilters.withVideos ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-2xl shadow-md transform transition-transform ${
                    selectedFilters.withVideos
                      ? "translate-x-5 sm:translate-x-6"
                      : "translate-x-0.5"
                  } mt-0.5`}
                ></div>
              </div>
            </div>
          </label>
        </div>

        {/* Bottom Buttons for Mobile */}
        <div className="lg:hidden p-4 border-t border-gray-200 sticky bottom-0 bg-white flex gap-2">
          <button
            onClick={resetFilters}
            className="w-full bg-red-500 text-white font-semibold py-3 rounded-lg hover:bg-red-600 transition-colors"
          >
            Reset
          </button>
          <button
            onClick={handleCancelFilter}
            className="w-full bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={applyFilters}
            className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="w-full">
      <div className="hidden lg:block bg-white rounded-lg overflow-hidden border border-gray-200">
        <FilterContent />
      </div>

      <div className="lg:hidden">
        <button
          onClick={() => setIsOpen(true)}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Sliders className="w-5 h-5" />
          <span>Filter Properties</span>
        </button>

        {isOpen && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/50 transition-opacity"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute inset-0 flex flex-col max-w-full">
              <div className="sm:ml-auto sm:w-1/2 bg-white h-full overflow-hidden flex flex-col">
                <FilterContent />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandFilter;
